import * as React from "react";
import Header from "@amzn/awsui-components-react/polaris/header";
import Table from "@amzn/awsui-components-react/polaris/table";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Select from "@amzn/awsui-components-react/polaris/select";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Button from "@amzn/awsui-components-react/polaris/button";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import { API } from "aws-amplify";
import {
  INSURANCE_STATE_VALUES,
  INSURANCE_TYPE_VALUES,
  isDeprecatedValidationType,
  PRESCRIPTION_FILL_STATUS_VALUES,
  PRESCRIPTION_RX_STATUS_VALUES,
} from "./validation-types";

type ActionType = 'NOT_AVAILABLE' | 'PHARMACY_ACTION' | 'VALIDATION';

interface ActionConfig {
  operation: string;
  type: ActionType;
  inputFields: string[];
  outputFields: string[];
}

interface State {
  actions: ActionConfig[];
  validationTypes: string[];
  loading: boolean;
  saving: string | null;
  error: string | null;
  success: string | null;
}

const STUBBED_ACTIONS = ['UPDATE_FILL', 'EXPEDITE_ORDER', 'UPDATE_NBD', 'SHIPMENT_COMPLETE', 'MODIFY_KAMINO_CUSTOMER'];

const TYPE_OPTIONS = [
  { label: "Not Available", value: "NOT_AVAILABLE" },
  { label: "Post-Creation Action", value: "PHARMACY_ACTION" },
];

class ActionManager extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = { actions: [], validationTypes: [], loading: true, saving: null, error: null, success: null };
  }

  componentDidMount() { this.loadActions(); }

  loadActions = async () => {
    this.setState({ loading: true, error: null });
    try {
      const [postActionsResult, opsResult] = await Promise.all([
        API.get("ApaqPortalAPI", "/cpcp/list-post-creation-actions", {}),
        API.get("ApaqPortalAPI", "/operations/list", {}),
      ]);
      const postActions: ActionConfig[] = (postActionsResult.actions || []).map((a: any) => ({
        operation: a.name, type: 'PHARMACY_ACTION' as ActionType,
        inputFields: a.userInputs || [],
        outputFields: a.autoResolvedInputs || [],
      }));
      const rawOps = opsResult.operations || [];
      const validateOp: any = rawOps.find((o: any) => o.name === 'ValidateCustomer');
      const enumFields = validateOp?.enumFields || {};
      const vtypes: string[] = enumFields.validations || [];
      this.setState({ actions: postActions, validationTypes: vtypes, loading: false });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to load actions", loading: false });
    }
  };

  handleTypeChange = async (operation: string, newType: ActionType) => {
    const action = this.state.actions.find(a => a.operation === operation);
    if (!action) return;
    if (action.type === 'PHARMACY_ACTION' && newType !== 'PHARMACY_ACTION') {
      if (!window.confirm(`Disable ${operation}? Existing recipes using this action may break.`)) return;
    }
    this.setState({ saving: operation, error: null, success: null });
    try {
      await API.put("CpcpV2API", `/cpcp/v2/actions/${encodeURIComponent(operation)}`, {
        body: { operation, type: newType, inputFields: action.inputFields, outputFields: action.outputFields },
      });
      this.setState(prev => ({
        actions: prev.actions.map(a => a.operation === operation ? { ...a, type: newType } : a),
        saving: null,
        success: `Updated ${operation} to ${newType}`,
      }));
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : `Failed to update ${operation}`, saving: null });
    }
  };

  renderTable = (items: ActionConfig[], showTypeSelector: boolean) => (
    <Table
      loading={this.state.loading}
      items={items}
      columnDefinitions={[
        { id: "operation", header: "Operation", cell: (item: ActionConfig) => <b>{item.operation}</b>, width: 250 },
        { id: "status", header: "Status", cell: (item: ActionConfig) => STUBBED_ACTIONS.includes(item.operation) ? <Box color="text-status-warning">Stubbed</Box> : <Box color="text-status-success">Ready</Box>, width: 100 },
        ...(showTypeSelector ? [{
          id: "type", header: "Type", cell: (item: ActionConfig) => (
            <Select
              expandToViewport
              selectedOption={TYPE_OPTIONS.find(o => o.value === item.type) || null}
              onChange={({ detail }) => this.handleTypeChange(item.operation, detail.selectedOption.value as ActionType)}
              options={TYPE_OPTIONS}
              disabled={this.state.saving === item.operation}
            />
          ), width: 200,
        }] : []),
        { id: "input", header: "User Inputs", cell: (item: ActionConfig) => { const s = (item.inputFields || []).join(", "); return s || "None (auto-resolved)"; } },
        { id: "output", header: "Auto-Resolved from ctx", cell: (item: ActionConfig) => { const s = (item.outputFields || []).join(", "); return s || "—"; } },
      ]}
      variant="embedded"
    />
  );

  render() {
    const { actions, loading, error, success } = this.state;
    const pharmacyActions = actions.filter(a => a.type === 'PHARMACY_ACTION');
    // Validation types come from the ValidateCustomer ValidationType enum
    // (INSURANCE_TYPE_CHECK included). Filter out the deprecated aliases
    // (INSURANCE, PRESCRIPTION_STATUS) so authors are only offered the
    // canonical names.
    const validationActions = this.state.validationTypes
      .filter(t => !isDeprecatedValidationType(t))
      .map(name => ({ name }));

    return (
      <SpaceBetween size="l">
        <Header variant="h2" actions={<Button iconName="refresh" onClick={this.loadActions} loading={loading}>Refresh</Button>}>
          Action Manager
        </Header>

        {error && <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />}
        {success && <Flashbar items={[{ type: "success", content: success, dismissible: true, onDismiss: () => this.setState({ success: null }) }]} />}

        <Tabs tabs={[
          {
            id: "post-creation",
            label: `Post-Creation Actions (${pharmacyActions.length})`,
            content: (
              <SpaceBetween size="m">
                <Box color="text-body-secondary" fontSize="body-s">
                  Available as recipe steps. Called by the Step Function after base customer creation.
                </Box>
                {this.renderTable(pharmacyActions, false)}
              </SpaceBetween>
            ),
          },
          {
            id: "validation",
            label: `Validation Types (${validationActions.length})`,
            content: (
              <SpaceBetween size="m">
                <Box color="text-body-secondary" fontSize="body-s">
                  Available as validation checks in the validate step. Defined in the ValidateCustomer operation's ValidationType enum; INSURANCE_TYPE_CHECK is validated by ApaqPortal against the customer's insurances.
                </Box>
                <Table loading={loading} items={validationActions} columnDefinitions={[
                  { id: "name", header: "Validation Type", cell: (item: any) => <b>{item.name}</b>, width: 300 },
                  { id: "expected", header: "Expected Input", cell: (item: any) => {
                    const inputs: Record<string, string> = {
                      PRESCRIPTION_COUNT: "count (e.g., 1, 2, 3)",
                      INSURANCE_COUNT: "count (e.g., 1, 2, 3)",
                      PRESCRIPTION_FILL_STATUS: PRESCRIPTION_FILL_STATUS_VALUES.join(", "),
                      PRESCRIPTION_RX_STATUS: PRESCRIPTION_RX_STATUS_VALUES.join(", "),
                      CUSTOMER_PREFERENCES_EXIST: "true / false",
                      PROFILE_EXISTS: "true / false",
                      CONTRACT_ENROLLED: "true / false",
                      INSURANCE_TYPE_CHECK: `object: insuranceState (required: ${INSURANCE_STATE_VALUES.join(", ")}), insuranceType (${INSURANCE_TYPE_VALUES.join(", ")}), cardHolderId, binNumber, groupNumber, pcn`,
                    };
                    return inputs[item.name] || "—";
                  }},
                ]} variant="embedded" />
              </SpaceBetween>
            ),
          },
        ]} />
      </SpaceBetween>
    );
  }
}

export default ActionManager;
